import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  getSystemEmail,
  getSystemEmails,
  previewSystemEmail,
  resetSystemEmail,
  startFromTemplate,
  updateSystemEmail,
} from '../services/system-emails.service';
import type {
  StartFromTemplateInput,
  StartFromTemplateResponse,
  SystemEmailDetail,
  SystemEmailPreview,
  SystemEmailSummary,
  UpdateSystemEmailInput,
} from '../types/system-emails.types';

export const SYSTEM_EMAILS_QUERY_KEY = ['settings', 'system-emails'] as const;

export const useGetSystemEmails = () =>
  useGlobalQuery<SystemEmailSummary[]>({
    queryKey: SYSTEM_EMAILS_QUERY_KEY,
    queryFn: getSystemEmails,
    staleTime: 30_000,
  });

export const useGetSystemEmail = (key: string, locale: string) =>
  useGlobalQuery<SystemEmailDetail>({
    queryKey: [...SYSTEM_EMAILS_QUERY_KEY, key, locale],
    queryFn: () => getSystemEmail(key, locale),
    staleTime: 30_000,
  });

export const useUpdateSystemEmail = (key: string, locale: string) => {
  const queryClient = useQueryClient();
  return useGlobalMutation<SystemEmailDetail, Error, UpdateSystemEmailInput>({
    mutationFn: (input) => updateSystemEmail(key, locale, input),
    onSuccess: (email) => {
      queryClient.setQueryData([...SYSTEM_EMAILS_QUERY_KEY, key, locale], email);
      void queryClient.invalidateQueries({ queryKey: SYSTEM_EMAILS_QUERY_KEY });
    },
    suppressToast: true,
  });
};

export const useResetSystemEmail = (key: string, locale: string) => {
  const queryClient = useQueryClient();
  return useGlobalMutation<SystemEmailDetail, Error, void>({
    mutationFn: () => resetSystemEmail(key, locale),
    onSuccess: (email) => {
      queryClient.setQueryData([...SYSTEM_EMAILS_QUERY_KEY, key, locale], email);
      void queryClient.invalidateQueries({ queryKey: SYSTEM_EMAILS_QUERY_KEY });
    },
    suppressToast: true,
  });
};

export const usePreviewSystemEmail = (key: string, locale?: string, enabled = true) =>
  useGlobalQuery<SystemEmailPreview>({
    queryKey: [...SYSTEM_EMAILS_QUERY_KEY, key, locale ?? 'default', 'preview'],
    queryFn: () => previewSystemEmail(key, locale),
    enabled: Boolean(key && enabled),
    staleTime: 30_000,
  });

export const useStartFromTemplate = (key: string) => {
  return useGlobalMutation<StartFromTemplateResponse, Error, StartFromTemplateInput>({
    mutationFn: (input) => startFromTemplate(key, input),
    suppressToast: true,
  });
};

